/**
 * Settings → IA → Uso: which provider and model actually ran each task, how
 * often it failed, what it consumed and when it last ran.
 *
 * The product had no answer to any of that: the outage that motivated
 * per-task routing was diagnosed from Cloud Functions logs, and a task can
 * silently land on its fallback provider. Tokens are shown, never a
 * currency — the providers do not publish prices through their APIs.
 */

import React, { useEffect, useState } from 'react';
import {
  AI_TASKS,
  AI_USAGE_WINDOWS,
  isAiTaskId,
  type AiUsageGroup,
  type AiUsageSummary,
  type AiUsageWindow,
  type SettingsAiProvider,
} from '@zunelo/shared';
import { settingsClient, shouldUseMockSettings } from '../../services/clients';
import { AI_PROVIDER_LABELS } from './aiSettingsLabels';
import { hintStyle, inputStyle, labelStyle } from './settingsStyles';

const WINDOW_LABELS: Record<AiUsageWindow, string> = {
  7: 'Últimos 7 días',
  30: 'Últimos 30 días',
  90: 'Últimos 90 días',
};

/** Non-task calls the ledger still attributes (see `AiUsageTask`). */
const NON_TASK_LABELS: Record<string, string> = {
  connectionProbe: 'Probar conexión',
  unknown: 'Sin tarea declarada',
};

export interface SettingsAiUsageBlockProps {
  /** Providers with a stored credential, so an empty ledger can explain itself. */
  configuredProviders?: readonly SettingsAiProvider[];
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

function formatCount(value: number): string {
  return value.toLocaleString('es-ES');
}

/** Same locale as the counts, so `.` never means two things in one sentence. */
function formatScaled(value: number, suffix: string): string {
  return `${value.toLocaleString('es-ES', { maximumFractionDigits: 1 })} ${suffix}`;
}

function formatTokens(value: number): string {
  if (value >= 1_000_000) return formatScaled(value / 1_000_000, 'M');
  if (value >= 1_000) return formatScaled(value / 1_000, 'k');
  return formatCount(value);
}

function formatLatency(ms: number): string {
  return ms >= 1_000 ? formatScaled(ms / 1_000, 's') : `${formatCount(ms)} ms`;
}

function formatDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/** Adds the year only when the window crosses one, to keep it short. */
function formatRange(since: string, until: string): string {
  const sameYear = since.slice(0, 4) === until.slice(0, 4);
  const withYear = (day: string) => {
    const date = new Date(`${day}T00:00:00.000Z`);
    if (Number.isNaN(date.getTime())) return day;
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      timeZone: 'UTC',
    });
  };
  return sameYear
    ? `${formatDay(since)} → ${formatDay(until)}`
    : `${withYear(since)} → ${withYear(until)}`;
}

function formatLastAt(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}

/** "Ruta elegida: 6 llamadas, 3 fallidas" — attempts *and* what they answered. */
function attemptSummary(group: AiUsageGroup | undefined): string {
  const label = group?.key === 'fallback' ? 'Fallback' : 'Ruta elegida';
  if (!group) return `${label}: sin llamadas`;
  return `${label}: ${plural(group.calls, 'llamada', 'llamadas')}, ${formatCount(
    group.failures,
  )} ${group.failures === 1 ? 'fallida' : 'fallidas'}`;
}

/**
 * What a stored provider status means for the user. The ledger keeps only the
 * status (never the provider's message), so this mapping is what turns it into
 * something actionable: a quota to raise vs a credential to fix.
 */
function errorReason(status: number | undefined): string {
  if (status === 429) return 'límite de cuota';
  if (status === 401 || status === 403) return 'credencial rechazada';
  if (status && status >= 500) return 'error del proveedor';
  if (status && status >= 400) return `rechazado por el proveedor (${status})`;
  // `0` is what a network failure or a timeout records; `undefined` means the
  // stored value could not be read, which is a different thing.
  return status === 0 ? 'sin respuesta' : 'causa desconocida';
}

/** Rows carry Spanish labels, never the internal ids used on the wire. */
function groupLabel(group: AiUsageGroup): string {
  if (isAiTaskId(group.key)) return AI_TASKS[group.key].label;
  if (NON_TASK_LABELS[group.key]) return NON_TASK_LABELS[group.key];
  const provider = group.key as SettingsAiProvider;
  if (AI_PROVIDER_LABELS[provider]) return AI_PROVIDER_LABELS[provider];
  // Model rows arrive as `<provider> · <model>`; only the provider is renamed.
  const [head, ...rest] = group.key.split(' · ');
  const label = AI_PROVIDER_LABELS[head as SettingsAiProvider];
  return label && rest.length ? [label, ...rest].join(' · ') : group.label;
}

const cellStyle: React.CSSProperties = {
  padding: '6px 8px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--z-text)',
  borderBottom: '1px solid var(--z-border)',
  whiteSpace: 'nowrap',
};

const headCellStyle: React.CSSProperties = {
  ...cellStyle,
  color: 'var(--z-text-2)',
  fontWeight: 600,
};

function UsageTable({
  caption,
  dimension,
  groups,
  testId,
}: {
  caption: string;
  dimension: string;
  groups: AiUsageGroup[];
  testId: string;
}) {
  if (!groups.length) return null;
  return (
    // Wide tables scroll inside their own box so the modal never scrolls
    // sideways; the box is focusable, or a keyboard user could not reach the
    // columns that overflow.
    <div
      tabIndex={0}
      role="group"
      aria-label={caption}
      style={{ overflowX: 'auto', outlineOffset: 2 }}
    >
      <table data-testid={testId} style={{ borderCollapse: 'collapse', width: '100%' }}>
        <caption style={{ ...labelStyle, textAlign: 'left', paddingBottom: 4 }}>{caption}</caption>
        <thead>
          <tr>
            <th scope="col" style={headCellStyle}>
              {dimension}
            </th>
            <th scope="col" style={headCellStyle}>
              Llamadas
            </th>
            <th scope="col" style={headCellStyle}>
              De ellas fallidas
            </th>
            <th scope="col" style={headCellStyle}>
              Entrada
            </th>
            <th scope="col" style={headCellStyle}>
              Salida
            </th>
            <th scope="col" style={headCellStyle}>
              Total
            </th>
            <th scope="col" style={headCellStyle}>
              Latencia media
            </th>
            <th scope="col" style={headCellStyle}>
              Último uso
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.key} data-testid={`${testId}-row-${group.key}`}>
              <th scope="row" style={{ ...cellStyle, fontWeight: 400 }}>
                {groupLabel(group)}
              </th>
              <td style={cellStyle}>{formatCount(group.calls)}</td>
              <td
                style={{
                  ...cellStyle,
                  color: group.failures ? 'var(--z-bad-ink)' : 'var(--z-text)',
                  fontWeight: group.failures ? 700 : 400,
                }}
              >
                {group.failures
                  ? `⚠ ${formatCount(group.failures)} · último fallo: ${errorReason(
                      group.lastErrorStatus,
                    )}`
                  : '0'}
              </td>
              <td style={cellStyle}>{formatTokens(group.promptTokens)}</td>
              <td style={cellStyle}>{formatTokens(group.completionTokens)}</td>
              <td style={cellStyle}>{formatTokens(group.totalTokens)}</td>
              <td style={cellStyle}>{group.calls ? formatLatency(group.avgLatencyMs) : '—'}</td>
              <td style={cellStyle}>{formatLastAt(group.lastAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The figures for one day, in the same units and order as the tables. */
function dayReadout(group: AiUsageGroup): string {
  return [
    `${formatDay(group.key)}: ${plural(group.calls, 'llamada', 'llamadas')}`,
    `${formatCount(group.failures)} de ellas ${group.failures === 1 ? 'fallida' : 'fallidas'}`,
  ]
    .join(', ')
    .concat(
      ` · ${formatTokens(group.totalTokens)} tokens · ${formatLatency(group.avgLatencyMs)} de media`,
    );
}

/**
 * Per-day bars: the shape of a month is faster to read than 30 numbers, but
 * the numbers have to be reachable or the strip is decoration. Hovering,
 * clicking or arrowing across a bar writes that day's figures into a readout
 * below; a hover-only `title` left them invisible on touch and to a keyboard.
 *
 * The strip stays a single tab stop with arrow-key navigation instead of
 * making 90 bars focusable, which would bury every control after it.
 */
function UsageByDay({ groups, peakLabel }: { groups: AiUsageGroup[]; peakLabel: string }) {
  const peak = groups.reduce((max, group) => Math.max(max, group.calls), 0);
  const [pinned, setPinned] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  const active = hovered ?? pinned;
  const activeGroup = active === null ? undefined : groups[active];
  const step = (delta: number) =>
    setPinned((current) => {
      const next = (current ?? -1) + delta;
      return Math.max(0, Math.min(groups.length - 1, next));
    });
  if (!peak) return null;
  return (
    <div data-testid="settings-ai-usage-by-day" style={{ display: 'grid', gap: 4 }}>
      <span style={labelStyle}>Por día</span>
      <div
        tabIndex={0}
        role="group"
        data-testid="settings-ai-usage-day-strip"
        aria-label={`Llamadas por día. ${peakLabel} Usa las flechas para recorrer los días.`}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          // The strip owns the arrows while it has focus; letting them also
          // scroll the modal would fight the navigation.
          event.preventDefault();
          step(event.key === 'ArrowRight' ? 1 : -1);
        }}
        onMouseLeave={() => setHovered(null)}
        style={{ overflowX: 'auto', outlineOffset: 2 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 2,
            height: 48,
            // 90 two-pixel bars are unreadable on a phone; scroll instead.
            minWidth: groups.length * 6,
          }}
        >
          {groups.map((group, index) => {
            const label = `${formatDay(group.key)}: ${plural(group.calls, 'llamada', 'llamadas')}, ${plural(
              group.failures,
              'fallo',
              'fallos',
            )}`;
            return (
              <div
                key={group.key}
                role="img"
                title={label}
                aria-label={label}
                onMouseEnter={() => setHovered(index)}
                onClick={() => setPinned(index)}
                style={{
                  flex: 1,
                  minWidth: 4,
                  alignSelf: 'flex-end',
                  cursor: 'pointer',
                  outline: active === index ? '1px solid var(--z-accent)' : 'none',
                  outlineOffset: 1,
                  height: group.calls
                    ? `${Math.max(6, Math.round((group.calls / peak) * 100))}%`
                    : 2,
                  background: group.failures
                    ? // Stripes, so "this day failed" survives without colour.
                      'repeating-linear-gradient(45deg, var(--z-bad-ink) 0 3px, var(--z-bad) 3px 6px)'
                    : group.calls
                      ? 'var(--z-accent)'
                      : 'var(--z-border)',
                  borderRadius: 2,
                }}
              />
            );
          })}
        </div>
      </div>
      {/*
        The peak hint and the per-day figures share one line: two lines that
        swap would make the block jump as the pointer crosses the strip.
      */}
      <span data-testid="settings-ai-usage-day-readout" aria-live="polite" style={hintStyle}>
        {activeGroup ? dayReadout(activeGroup) : peakLabel}
      </span>
    </div>
  );
}

export const SettingsAiUsageBlock: React.FC<SettingsAiUsageBlockProps> = ({
  configuredProviders = [],
}) => {
  const [days, setDays] = useState<AiUsageWindow>(30);
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // Bumped by Reintentar: without it the only way to retry is to change the
  // window and change it back.
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    // The error line (and its focused Reintentar button) stays until the retry
    // answers: clearing it here would unmount the button mid-click.
    if (typeof settingsClient.getAiUsage !== 'function') {
      setUnavailable(true);
      setLoading(false);
      return undefined;
    }
    void settingsClient
      .getAiUsage(days)
      .then(({ data }) => {
        if (!active) return;
        setSummary(data);
        setError(null);
      })
      .catch((cause: unknown) => {
        // Usage is diagnostic, never blocking: say it could not be read and
        // drop the previous window's numbers, which would otherwise be shown
        // under the new window's label. Clearing the summary is the *only*
        // guard on purpose — a second one in the render condition made
        // neither of them provably necessary.
        if (!active) return;
        setSummary(null);
        // The local BFF keeps no ledger and says so; that is a different
        // answer from "the read failed".
        if ((cause as { code?: string })?.code === 'AI_USAGE_UNAVAILABLE') {
          setUnavailable(true);
          setError(null);
          return;
        }
        setError('No se pudo leer el uso de IA.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [days, reloadNonce]);

  const totals = summary?.totals;
  const attemptGroup = (key: 'primary' | 'fallback') =>
    summary?.byAttempt.find((group) => group.key === key);
  const attempts = {
    primary: attemptGroup('primary'),
    fallback: attemptGroup('fallback'),
  };
  const peak = summary?.byDay.reduce((max, group) => Math.max(max, group.calls), 0) ?? 0;

  return (
    <section
      data-testid="settings-ai-usage"
      style={{
        display: 'grid',
        gap: 10,
        marginTop: 4,
        paddingTop: 16,
        borderTop: '1px solid var(--z-border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={labelStyle}>Uso</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={hintStyle}>Ventana</span>
          <select
            data-testid="settings-ai-usage-window"
            disabled={unavailable}
            value={days}
            onChange={(event) => setDays(Number(event.target.value) as AiUsageWindow)}
            style={{ ...inputStyle, width: 'auto' }}
          >
            {AI_USAGE_WINDOWS.map((option) => (
              <option key={option} value={option}>
                {WINDOW_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
        {shouldUseMockSettings() ? (
          <span data-testid="settings-ai-usage-demo" style={hintStyle}>
            Datos de demostración.
          </span>
        ) : null}
      </div>

      {/* The numbers are replaced when the window changes; announce it. */}
      <div aria-live="polite">
        {loading ? (
          <p data-testid="settings-ai-usage-loading" style={hintStyle}>
            Cargando el uso…
          </p>
        ) : null}
        {error ? (
          <p
            data-testid="settings-ai-usage-error"
            style={{ ...hintStyle, color: 'var(--z-bad-ink)', display: 'flex', gap: 8 }}
          >
            {error}
            <button
              type="button"
              data-testid="settings-ai-usage-retry"
              disabled={loading}
              onClick={() => setReloadNonce((value) => value + 1)}
              style={{
                border: 0,
                background: 'transparent',
                padding: 0,
                color: 'var(--z-accent)',
                cursor: 'pointer',
              }}
            >
              Reintentar
            </button>
          </p>
        ) : null}
        {unavailable && !loading ? (
          <p data-testid="settings-ai-usage-unavailable" style={hintStyle}>
            El registro de uso no está disponible en este entorno.
          </p>
        ) : null}
        {totals && totals.calls === 0 ? (
          <p data-testid="settings-ai-usage-empty" style={hintStyle}>
            {configuredProviders.length
              ? 'Todavía no hay llamadas de IA registradas en esta ventana.'
              : 'Conecta un proveedor en Proveedores para empezar a registrar uso.'}
          </p>
        ) : null}
        {totals && totals.calls > 0 ? (
          <p data-testid="settings-ai-usage-totals" style={hintStyle}>
            {plural(totals.calls, 'llamada', 'llamadas')}, {formatCount(totals.failures)} de ellas{' '}
            {totals.failures === 1 ? 'fallida' : 'fallidas'} · {formatTokens(totals.totalTokens)}{' '}
            tokens · {formatLatency(totals.avgLatencyMs)} de media ·{' '}
            {formatRange(summary.since, summary.until)}.
          </p>
        ) : null}
      </div>

      {totals && totals.calls > 0 ? (
        <>
          {attempts.fallback ? (
            <p data-testid="settings-ai-usage-attempts" style={hintStyle}>
              {attemptSummary(attempts.primary)} · {attemptSummary(attempts.fallback)}.
              {attempts.primary && attempts.primary.calls === attempts.primary.failures
                ? // The window this panel exists for: the route you chose ran
                  // and answered nothing.
                  ' La ruta que elegiste no respondió ninguna vez.'
                : ''}
            </p>
          ) : null}
          <UsageByDay
            groups={summary.byDay}
            peakLabel={`Máximo ${plural(peak, 'llamada', 'llamadas')} en un día.`}
          />
          <UsageTable
            caption="Uso por proveedor"
            dimension="Proveedor"
            groups={summary.byProvider}
            testId="settings-ai-usage-providers"
          />
          <UsageTable
            caption="Uso por modelo"
            dimension="Modelo"
            groups={summary.byModel}
            testId="settings-ai-usage-models"
          />
          <UsageTable
            caption="Uso por tarea"
            dimension="Tarea"
            groups={summary.byTask}
            testId="settings-ai-usage-tasks"
          />
          <p style={hintStyle}>
            La latencia media incluye las llamadas fallidas. Solo se guardan contadores: proveedor,
            modelo, tarea, tokens y latencia. Nunca el contenido de los prompts ni de las
            respuestas.
            {shouldUseMockSettings() ? ' Estos datos son de demostración.' : ''}
          </p>
        </>
      ) : null}
    </section>
  );
};
